import React, { useEffect, useRef } from 'react';
import { gantt } from 'dhtmlx-gantt';
import 'dhtmlx-gantt/codebase/dhtmlxgantt.css';
import './GanttChart.css';
import { formatDate, formatShortDate } from '../../../../utils/dateUtils';

const GanttChart = ({ tasks, onTaskClick, timeScale }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // Clear any existing instance
    gantt.clearAll();

    // Basic configuration
    gantt.config.date_format = '%Y-%m-%d %H:%i:%s';
    gantt.config.rtl = true;
    gantt.config.show_progress = false;
    gantt.config.row_height = 35;
    gantt.config.autosize = 'y';
    gantt.config.duration_unit = 'day';

    gantt.attachEvent('onGridHeaderClick', function (name, e) {
      if (name === 'add') {
        e.preventDefault();
        onTaskClick(null); // Pass null to indicate this is a new task
        return false;
      }
      return true;
    });

    // Hide time in tooltips and grid
    gantt.templates.task_date = date => {
      return formatShortDate(date);
    };

    gantt.templates.tooltip_date_format = date => {
      return formatShortDate(date);
    };

    // Configure scales based on timeScale prop
    if (timeScale === 'week') {
      gantt.config.scales = [
        {
          unit: 'month',
          step: 1,
          format: date => {
            return formatDate(date, 'MMMM yyyy');
          },
        },
        {
          unit: 'week',
          step: 1,
          format: date => {
            const start = new Date(date);
            const end = new Date(date);
            end.setDate(start.getDate() + 6);

            // Format dates as DD/MM
            const startStr = `${start.getDate()}/${start.getMonth() + 1}`;
            const endStr = `${end.getDate()}/${end.getMonth() + 1}`;

            return `${startStr} - ${endStr}`;
          },
        },
      ];
      gantt.config.scale_height = 60;
      gantt.config.min_column_width = 70;
    } else {
      gantt.config.scales = [
        {
          unit: 'year',
          step: 1,
          format: date => {
            return formatDate(date, 'yyyy');
          },
        },
        {
          unit: 'month',
          step: 1,
          format: date => {
            return formatDate(date, 'MMMM');
          },
        },
      ];
      gantt.config.scale_height = 60;
      gantt.config.min_column_width = 100;
    }

    // Calculate min/max dates from tasks
    const dates = tasks.data
      .map(task => [new Date(task.start_date), new Date(task.end_date)])
      .flat();
    const minDate = new Date(Math.min(...dates));
    const maxDate = new Date(Math.max(...dates));

    gantt.config.start_date = minDate;
    gantt.config.end_date = maxDate;

    // Initialize gantt in container
    gantt.init(containerRef.current);

    // Load data
    gantt.parse(tasks);

    // Event handlers
    const clickHandler = gantt.attachEvent('onTaskClick', id => {
      onTaskClick(id);
      return true;
    });

    // Cleanup
    return () => {
      gantt.detachEvent(clickHandler);
      gantt.clearAll();
    };
  }, [tasks, timeScale, onTaskClick]);

  return <div ref={containerRef} className="gantt-container" />;
};

export default GanttChart;
